import React, { useState, useMemo, useEffect } from 'react';
import './TraineeList.css';

interface WeeklyTrainee {
  firstName: string;
  lastName: string;
  department: string;
  weeklyHours?: number;
  status: 'At Risk' | 'Not At Risk' | 'N/A';
  riskScore?: number;
  attendanceRate?: number;
  absentRate?: number;
  holidayRatio?: number;
  weekNumber?: number;
}

interface WeeklyStudentsPanelProps {
  trainees: WeeklyTrainee[];
  loading?: boolean;
  filterAtRisk?: boolean;
}

const WeeklyStudentsPanel: React.FC<WeeklyStudentsPanelProps> = ({ 
  trainees, 
  loading = false,
  filterAtRisk = false
}) => {
  // Extract unique week numbers from trainees
  const availableWeeks = useMemo(() => {
    const weeks = new Set<number>();
    trainees.forEach(trainee => {
      if (trainee.weekNumber !== undefined) {
        weeks.add(trainee.weekNumber);
      }
    });
    return Array.from(weeks).sort((a, b) => b - a); // Sort descending (most recent first)
  }, [trainees]);

  // Default to most recent week if available
  const defaultWeek = availableWeeks.length > 0 ? availableWeeks[0] : null;
  const [selectedWeek, setSelectedWeek] = useState<number | null>(defaultWeek);

  // Update selectedWeek when availableWeeks changes (e.g., on initial load)
  useEffect(() => {
    if (defaultWeek !== null && selectedWeek === null) {
      setSelectedWeek(defaultWeek);
    }
  }, [defaultWeek, selectedWeek]);

  // Filter trainees based on selected week and risk status
  const displayedTrainees = useMemo(() => {
    if (selectedWeek === null) {
      return [];
    }

    let filtered = trainees.filter(trainee => trainee.weekNumber === selectedWeek);

    // Filter by risk status if requested
    if (filterAtRisk) {
      filtered = filtered.filter(trainee => trainee.status === 'At Risk');
    }

    return filtered;
  }, [trainees, selectedWeek, filterAtRisk]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading trainee data...</p>
      </div>
    );
  }

  if (availableWeeks.length === 0) {
    return (
      <div className="no-data-message">
        No weekly trainee data available
      </div>
    );
  }

  if (displayedTrainees.length === 0 && selectedWeek !== null) {
    return (
      <div className="no-data-message">
        {filterAtRisk 
          ? `No at-risk students found for Week ${selectedWeek}`
          : `No trainee data available for Week ${selectedWeek}`}
      </div>
    );
  }

  return (
    <div className="trainee-table weekly-view">
      <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <label htmlFor="week-filter" style={{ fontWeight: '500', fontSize: '0.9rem' }}>
          Filter by Week:
        </label>
        <select
          id="week-filter"
          value={selectedWeek || ''}
          onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '0.9rem',
            cursor: 'pointer',
            minWidth: '150px'
          }}
          required
        >
          {availableWeeks.length === 0 && (
            <option value="">No weeks available</option>
          )}
          {availableWeeks.map(week => (
            <option key={week} value={week}>
              Week {week}
            </option>
          ))}
        </select>
        {selectedWeek !== null && (
          <span style={{ fontSize: '0.85rem', color: '#666' }}>
            Showing {displayedTrainees.length} trainee{displayedTrainees.length !== 1 ? 's' : ''} for Week {selectedWeek}
          </span>
        )}
      </div>
      <table>
        <thead>
          <tr>
            <th>Week</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Department</th>
            <th>Weekly Hours</th>
            <th>Attendance Rate</th>
            <th>Risk Score</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {displayedTrainees.map((trainee, index) => (
            <tr key={index}>
              <td>Week {trainee.weekNumber}</td>
              <td>{trainee.firstName}</td>
              <td>{trainee.lastName}</td>
              <td>{trainee.department}</td>
              <td>
                {trainee.weeklyHours !== undefined 
                  ? `${trainee.weeklyHours} hours` 
                  : 'N/A'
                }
              </td>
              <td>
                {trainee.attendanceRate !== undefined 
                  ? `${Math.round(trainee.attendanceRate * 100)}%` 
                  : 'N/A'
                }
              </td>
              <td>
                {trainee.riskScore !== undefined 
                  ? trainee.riskScore.toFixed(2)
                  : 'N/A'
                }
              </td>
              <td>
                {trainee.status === 'N/A' ? (
                  <span className="status-badge n-a">N/A</span>
                ) : (
                  <span className={`status-badge ${trainee.status === 'At Risk' ? 'at-risk' : 'not-at-risk'}`}>
                    {trainee.status}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WeeklyStudentsPanel;

